import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  ArrowLeft,
  Download,
  Edit,
  FileText,
  ExternalLink,
  FileX,
  Upload,
  Link2,
  PackageOpen,
  Trash2,
  Clock,
  Shield,
} from 'lucide-react';
import {
  certificatesApi,
  CertificateItem,
  formatCertificateType,
  isExpiringSoon,
  getDaysUntilExpiry,
} from '../api/services/certificates.api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  PageHeader,
  StatusBadge,
  MetricCard,
  KPIGrid,
  DataTable,
  Column,
} from '../components/common';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LinkBatchForm } from '../components/certificates/LinkBatchForm';

// Certificate status mapping for StatusBadge
const certificateStatusMap: Record<string, string> = {
  VALID: 'approved',
  ACTIVE: 'approved',
  EXPIRED: 'expired',
  REVOKED: 'cancelled',
  PENDING_VERIFICATION: 'pending',
  PENDING_RENEWAL: 'pending',
};

export default function CertificateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [linkBatchDialogOpen, setLinkBatchDialogOpen] = useState(false);

  const { data: certificate, isLoading } = useQuery({
    queryKey: ['certificate', id],
    queryFn: () => certificatesApi.getById(id!),
  });

  const deleteMutation = useMutation({
    mutationFn: (certificateId: string) => certificatesApi.delete(certificateId),
    onSuccess: () => {
      toast.success('Certificate deleted successfully');
      navigate('/certificates');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete certificate');
    },
  });

  const handleDownload = async () => {
    if (!certificate?.id) return;
    try {
      const blob = await certificatesApi.downloadDocument(certificate.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = certificate.documentName || `${certificate.certificateNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download document');
    }
  };

  const handleDelete = () => {
    if (
      window.confirm(
        'Are you sure you want to delete this certificate? This action cannot be undone.'
      )
    ) {
      deleteMutation.mutate(id!);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <FileX className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-semibold mb-2">Certificate Not Found</h2>
        <p className="text-muted-foreground mb-6">That certificate does not exist.</p>
        <Button onClick={() => navigate('/certificates')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Certificates
        </Button>
      </div>
    );
  }

  const linkedItemColumns: Column<CertificateItem>[] = [
    {
      key: 'batchNumber',
      header: 'Batch Number',
      render: (item) =>
        item.batchNumber ? (
          <span className="font-mono text-primary-600">{item.batchNumber}</span>
        ) : (
          <>-</>
        ),
    },
    {
      key: 'item',
      header: 'Item',
      render: (item) =>
        item.item ? (
          <Link to={`/inventory/${item.item.id}`} className="text-primary-600 hover:underline">
            {item.item.name}
          </Link>
        ) : (
          <>-</>
        ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (item) => <>{item.quantity || '-'}</>,
    },
    {
      key: 'heatLot',
      header: 'Heat / Lot #',
      render: (item) => (
        <div className="text-sm">
          {item.heatNumber && <div>Heat: {item.heatNumber}</div>}
          {item.lotNumber && <div>Lot: {item.lotNumber}</div>}
          {!item.heatNumber && !item.lotNumber && '-'}
        </div>
      ),
    },
    {
      key: 'serialNumber',
      header: 'Serial Number',
      render: (item) => <>{item.serialNumber || '-'}</>,
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      {/* Header */}
      <motion.div variants={fadeUp}>
        <PageHeader
          backLink={{ label: 'Back to Certificates', href: '/certificates' }}
          title={certificate.certificateNumber}
          description={`${formatCertificateType(certificate.type)}${certificate.subType ? ` - EN 10204 Type ${certificate.subType}` : ''} • Issued ${format(new Date(certificate.issuedDate), 'MMM dd, yyyy')}`}
          actions={
            <div className="flex gap-2">
              {certificate.documentUrl && (
                <Button onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Document
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => toast.info('Edit functionality coming soon')}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* Status Badges */}
      <motion.div variants={fadeUp} className="flex items-center gap-2 mt-6">
        <StatusBadge
          status={certificateStatusMap[certificate.status] || 'pending'}
          label={certificate.status.replace(/_/g, ' ')}
        />
        {certificate.expiryDate && isExpiringSoon(certificate.expiryDate, 90) && (
          <StatusBadge
            status="critical"
            label={`Expires ${formatDistanceToNow(new Date(certificate.expiryDate), { addSuffix: true })}`}
          />
        )}
      </motion.div>

      {/* Certificate Metrics */}
      <KPIGrid columns={4}>
        <MetricCard
          label="Linked Batches"
          value={certificate.items?.length || 0}
          sentiment="info"
          icon={<PackageOpen className="h-5 w-5" />}
        />
        <MetricCard
          label="Days Until Expiry"
          value={certificate.expiryDate ? getDaysUntilExpiry(certificate.expiryDate) : '∞'}
          sentiment={
            certificate.expiryDate && isExpiringSoon(certificate.expiryDate, 90)
              ? 'warning'
              : 'success'
          }
          icon={<Clock className="h-5 w-5" />}
        />
        <MetricCard
          label="Verification"
          value={certificate.verifiedAt ? 'Verified' : 'Pending'}
          sentiment={certificate.verifiedAt ? 'success' : 'warning'}
          icon={<Shield className="h-5 w-5" />}
        />
        <MetricCard
          label="Document"
          value={certificate.documentUrl ? 'Available' : 'Missing'}
          sentiment={certificate.documentUrl ? 'success' : 'warning'}
          icon={<FileText className="h-5 w-5" />}
        />
      </KPIGrid>

      {/* Tabs */}
      <motion.div variants={fadeUp} className="mt-6">
        <Tabs defaultValue="details" className="space-y-6">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="batches">
              Linked Batches ({certificate.items?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="document">Document</TabsTrigger>
            <TabsTrigger value="history">Audit Trail</TabsTrigger>
          </TabsList>

          {/* Tab 1: Details */}
          <TabsContent value="details" className="space-y-6">
            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Certificate Information</h3>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-6">
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">
                      Certificate Number
                    </dt>
                    <dd className="mt-1 font-mono">{certificate.certificateNumber}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Type</dt>
                    <dd className="mt-1">{formatCertificateType(certificate.type)}</dd>
                  </div>
                  {certificate.subType && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">EN 10204 Type</dt>
                      <dd className="mt-1">
                        {certificate.subType} -{' '}
                        {certificate.subType === '2.1'
                          ? 'Supplier Declaration'
                          : certificate.subType === '2.2'
                            ? 'Test Report'
                            : certificate.subType === '3.1'
                              ? 'Manufacturer Certificate'
                              : certificate.subType === '3.2'
                                ? 'Third-Party Certificate'
                                : certificate.subType}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Issue Date</dt>
                    <dd className="mt-1">
                      {format(new Date(certificate.issuedDate), 'MMMM dd, yyyy')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Expiry Date</dt>
                    <dd className="mt-1">
                      {certificate.expiryDate
                        ? format(new Date(certificate.expiryDate), 'MMMM dd, yyyy')
                        : 'No expiry'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                    <dd className="mt-1">
                      <StatusBadge
                        status={certificateStatusMap[certificate.status] || 'pending'}
                        label={certificate.status.replace(/_/g, ' ')}
                      />
                    </dd>
                  </div>
                  {certificate.issuingAuthority && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">
                        Issuing Authority
                      </dt>
                      <dd className="mt-1">{certificate.issuingAuthority}</dd>
                    </div>
                  )}
                  {certificate.manufacturerName && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">
                        Manufacturer / Mill
                      </dt>
                      <dd className="mt-1">{certificate.manufacturerName}</dd>
                    </div>
                  )}
                  {certificate.heatNumber && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Heat Number</dt>
                      <dd className="mt-1 font-mono text-primary-600">{certificate.heatNumber}</dd>
                    </div>
                  )}
                  {certificate.lotNumber && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Lot Number</dt>
                      <dd className="mt-1 font-mono text-primary-600">{certificate.lotNumber}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>

            {certificate.complianceStandards && certificate.complianceStandards.length > 0 && (
              <Card>
                <CardHeader>
                  <h3 className="text-base font-medium text-foreground">Compliance Standards</h3>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {certificate.complianceStandards.map((standard) => (
                      <Badge key={standard} variant="secondary">
                        {standard}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Upload Information</h3>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-6">
                  {certificate.uploadedBy && (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Uploaded By</dt>
                        <dd className="mt-1">{certificate.uploadedBy.fullName}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Upload Date</dt>
                        <dd className="mt-1">
                          {format(new Date(certificate.createdAt), 'MMMM dd, yyyy HH:mm')}
                        </dd>
                      </div>
                    </>
                  )}
                  {certificate.verifiedBy && (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Verified By</dt>
                        <dd className="mt-1">{certificate.verifiedBy.fullName}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">
                          Verification Date
                        </dt>
                        <dd className="mt-1">
                          {certificate.verifiedAt
                            ? format(new Date(certificate.verifiedAt), 'MMMM dd, yyyy HH:mm')
                            : '-'}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>

            {certificate.notes && (
              <Card>
                <CardHeader>
                  <h3 className="text-base font-medium text-foreground">Notes</h3>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{certificate.notes}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab 2: Linked Batches */}
          <TabsContent value="batches" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <h3 className="text-base font-medium text-foreground">
                  Batches Linked to This Certificate
                </h3>
                <Dialog open={linkBatchDialogOpen} onOpenChange={setLinkBatchDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Link2 className="h-4 w-4 mr-2" />
                      Link Batch
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <LinkBatchForm
                      certificateId={id!}
                      onSuccess={() => setLinkBatchDialogOpen(false)}
                    />
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <DataTable<CertificateItem>
                  pagination={false}
                  data={certificate.items || []}
                  columns={linkedItemColumns}
                  emptyTitle="No batches linked to this certificate"
                  emptyDescription="Link batches during goods receipt or using the button above"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Document */}
          <TabsContent value="document">
            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Certificate Document</h3>
              </CardHeader>
              <CardContent>
                {certificate.documentUrl ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 border rounded-lg">
                      <FileText className="h-12 w-12 text-primary-600" />
                      <div className="flex-1">
                        <p className="font-medium">{certificate.documentName}</p>
                        <p className="text-sm text-muted-foreground">
                          {certificate.documentMimeType} •{' '}
                          {certificate.documentSize
                            ? `${(certificate.documentSize / 1024).toFixed(2)} KB`
                            : 'Unknown size'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleDownload}>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            if (certificate.documentUrl) {
                              window.open(
                                `http://localhost:3000${certificate.documentUrl}`,
                                '_blank'
                              );
                            }
                          }}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open
                        </Button>
                      </div>
                    </div>

                    {/* PDF Preview */}
                    {certificate.documentMimeType === 'application/pdf' && (
                      <div className="border rounded-lg overflow-hidden">
                        <iframe
                          src={`http://localhost:3000${certificate.documentUrl}`}
                          className="w-full h-[600px]"
                          title="Certificate Preview"
                        />
                      </div>
                    )}

                    {/* Image Preview */}
                    {certificate.documentMimeType?.startsWith('image/') && (
                      <div className="border rounded-lg overflow-hidden">
                        <img
                          src={`http://localhost:3000${certificate.documentUrl}`}
                          alt="Certificate"
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileX className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No document attached to this certificate</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => toast.info('Upload document functionality coming soon')}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Document
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 4: Audit Trail */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Audit Trail</h3>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <p>Audit trail functionality coming soon</p>
                  <p className="text-sm mt-1">
                    This will show all changes made to this certificate
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}
